/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchJobTitles, saveJobTitle, clearJobTitleError } from '../store/slices/jobtitleSlice';
import { fetchDepartments } from '../store/slices/departmentSlice';
import Layout from '../common/components/Layout';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const JobTitle = () => {
  const dispatch = useDispatch();

  const { list: jobTitles = [], status } = useSelector(state => state.jobtitle || {});
  const { list: departments = [], status: deptStatus } = useSelector(state => state.department || {});

  const localCompanyId = localStorage.getItem("companyId") || "";

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingJob, setPendingJob] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '', title: '', departmentId: '', category: 'Staff', isActive: true
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });

  useEffect(() => {
    dispatch(fetchJobTitles());
    if (localCompanyId) {
      dispatch(fetchDepartments(localCompanyId));
    }
  }, [dispatch, localCompanyId]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, categoryFilter, statusFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-2 opacity-25" style={{ fontSize: '0.6rem' }}></i>;
    return sortConfig.direction === "asc" 
      ? <i className="bi bi-sort-down ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i> 
      : <i className="bi bi-sort-up ms-2 text-primary" style={{ fontSize: '0.7rem' }}></i>;
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    dispatch(clearJobTitleError());
    setIsEditMode(false);
    setFormData({ id: '', title: '', departmentId: '', category: 'Staff', isActive: true });
    if (localCompanyId) dispatch(fetchDepartments(localCompanyId));
    setShowModal(true);
  };

  const handleOpenEdit = item => {
    dispatch(clearJobTitleError());
    setIsEditMode(true);
    
    // FIX: Re-fetch departments for the specific company context if necessary
    const targetCompanyId = item.companyId || localCompanyId;
    if (targetCompanyId) dispatch(fetchDepartments(targetCompanyId));

    setFormData({ 
      id: item.id, 
      title: item.title, 
      // FIX: Map the ID correctly whether flat or nested
      departmentId: item.departmentId || item.department?.id || '', 
      category: item.category, 
      isActive: item.isActive 
    });
    setShowModal(true);
  };

const handleSave = async e => {
    e.preventDefault();
    
    const payload = { 
      title: formData.title,
      departmentId: formData.departmentId,
      category: formData.category,
      isActive: formData.isActive
    };

    if (isEditMode) payload.id = formData.id;

    try {
      await dispatch(saveJobTitle(payload)).unwrap();
      toast.success(`Job title "${payload.title}" saved!`);
      setShowModal(false);
      dispatch(fetchJobTitles());
    } catch (err) {
      console.error("Save Error:", err);
      const errorMessage = err?.message || err || "Failed to save";
      
      toast.error(errorMessage);
    }
  };

  const handleStatusToggle = (item) => {
    if (item.isActive) {
      setPendingJob(item);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(item);
    }
  };

  const executeStatusUpdate = async (item) => {
    try {
      await dispatch(saveJobTitle({ ...item, isActive: !item.isActive })).unwrap();
      toast.success(`${item.title} updated!`);
      setShowConfirm(false);
      dispatch(fetchJobTitles());
    } catch (err) {
      toast.error("Status update failed");
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(jobTitles) ? [...jobTitles] : [];
    
    items = items.filter(item => {
      const deptObj = departments.find(d => d.id === (item.departmentId || item.department?.id));
      const deptName = deptObj ? deptObj.departmentName : (item.department?.departmentName || '');
      
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = item.title.toLowerCase().includes(searchLower) || deptName.toLowerCase().includes(searchLower);
      const matchesCategory = categoryFilter === 'All' || item.category === categoryFilter;
      const matchesStatus = statusFilter === 'All' || (item.isActive ? 'Active' : 'Inactive') === statusFilter;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });

    return items.sort((a, b) => {
      let valA, valB;
      const key = sortConfig.key;
      if (key === 'department') {
        valA = (departments.find(d => d.id === (a.departmentId || a.department?.id))?.departmentName || "").toLowerCase();
        valB = (departments.find(d => d.id === (b.departmentId || b.department?.id))?.departmentName || "").toLowerCase();
      } else {
        valA = String(a[key] || '').toLowerCase();
        valB = String(b[key] || '').toLowerCase();
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [jobTitles, departments, searchTerm, categoryFilter, statusFilter, sortConfig]);

  const totalItems = processedData.length;
  const currentData = processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getPaginationGroup = () => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const totalDisplayed = 5; 
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages;
  };

  const inputStyle = { border: '1px solid rgba(0,0,0,0.1)', borderRadius: '8px', padding: '10px 14px', fontSize: '0.8rem', backgroundColor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(5px)', outline: 'none' };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="container-fluid py-4 flex-grow-1 px-lg-5 w-100">
        <style>{`
          .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; cursor: pointer; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
          .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; }
          .page-link-custom.active { background: #0f172a !important; color: #fff !important; }
          .page-link-custom.disabled { opacity: 0.3; cursor: not-allowed; }
          .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        `}</style>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0" style={{ letterSpacing: "-1.5px" }}>Job Titles</h1>
            <p className="text-muted small mb-0">Define and manage company positions</p>
          </div>
          <button className="btn btn-primary px-4 border-0" style={{ borderRadius: '8px', backgroundColor: '#0f172a', fontWeight: '600', fontSize: '0.85rem' }} onClick={handleOpenAdd}>
            <i className="bi bi-plus-lg me-2"></i>Add New
          </button>
        </div>

        <div className="row g-2 mb-4">
          <div className="col-md-6">
            <input type="text" className="form-control" placeholder="Search by title or department..." style={inputStyle} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div className="col-md-3">
            <select className="form-select" style={inputStyle} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="All">All Categories</option>
              {['Executive','Management','Supervisory','Professional','Staff'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <select className="form-select" style={inputStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(12px)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.3)", boxShadow: "0 10px 30px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3 border-0" onClick={() => requestSort("title")}>POSITION TITLE {getSortIcon("title")}</th>
                  <th className="py-3 border-0" onClick={() => requestSort("department")}>DEPARTMENT {getSortIcon("department")}</th>
                  <th className="py-3 border-0">CATEGORY</th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="pe-4 py-3 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: '0' }}>
                {currentData.map(item => {
                  const dept = departments.find(d => d.id === (item.departmentId || item.department?.id));
                  return (
                    <tr key={item.id} className="align-middle">
                      <td className="ps-4 py-3">
                        <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{item.title}</div>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#334155' }}>{dept ? dept.departmentName : (item.department?.departmentName || 'N/A')}</td>
                      <td>
                        <span className="badge border bg-light text-dark border-secondary-subtle" style={{ fontSize: '0.65rem', padding: '5px 10px' }}>
                          {item.category.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`badge rounded-pill ${item.isActive ? 'bg-success bg-opacity-10 text-success border border-success' : 'bg-danger bg-opacity-10 text-danger border border-danger'} border-opacity-25`} style={{ fontSize: '0.65rem', padding: '6px 12px' }}>
                          {toCamelCase(item.isActive ? 'Active' : 'Inactive')}
                        </span>
                      </td>
                      <td className="pe-4 text-end">
                        <div className="d-flex justify-content-end gap-2">
                          <button className="btn btn-sm btn-outline-primary px-3 fw-bold" style={{ borderRadius: '6px', fontSize: '0.75rem' }} onClick={() => handleOpenEdit(item)}>Edit</button>
                          <button className={`btn btn-sm ${item.isActive ? 'btn-outline-danger' : 'btn-outline-success'} px-3 fw-bold`} style={{ borderRadius: '6px', fontSize: '0.75rem', minWidth: '95px' }} onClick={() => handleStatusToggle(item)}>
                            {item.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <select className="form-select form-select-sm" style={{ width: 'auto', borderRadius: '6px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <span className="text-muted small">of <strong>{totalItems}</strong> entries</span>
            </div>
            <nav className="d-flex align-items-center">
              <button className={`page-link-custom ${currentPage === 1 ? 'disabled text-muted' : ''}`} onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
              {getPaginationGroup().map(p => (
                <button key={p} className={`page-link-custom ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
              ))}
              <button className={`page-link-custom ${currentPage === Math.ceil(totalItems / itemsPerPage) ? 'disabled text-muted' : ''}`} onClick={() => currentPage < Math.ceil(totalItems / itemsPerPage) && setCurrentPage(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
            </nav>
          </div>
        </div>
      </div>

      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 3000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg text-center" style={{ width: "400px" }}>
            <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: "3rem" }}></i>
            <h5 className="fw-bold mt-3">Confirm Deactivation</h5>
            <p className="text-muted small">Are you sure you want to deactivate <strong>{pendingJob?.title}</strong>?</p>
            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-danger w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => executeStatusUpdate(pendingJob)}>Yes, Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg border-0" style={{ width: '500px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
               <h5 className="fw-bold m-0 text-uppercase" style={{ fontSize: '1rem', letterSpacing: '1px' }}>{isEditMode ? 'Edit' : 'Add'} Job Title</h5>
               <button className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Position Title</label>
                  <input type="text" className="form-control" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                </div>
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Department</label>
                  <select className="form-select" required style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.departmentId} onChange={e => setFormData({...formData, departmentId: e.target.value})}>
                    <option value="">{deptStatus === 'loading' ? 'Loading...' : 'Select Department'}</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.departmentName}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="small fw-bold mb-1 text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Category</label>
                  <select className="form-select" style={{ border: "1px solid #ddd", borderRadius: "8px" }} value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                    {['Executive','Management','Supervisory','Professional','Staff'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <div className="form-check form-switch mt-2">
                    <input className="form-check-input" type="checkbox" checked={formData.isActive} onChange={e => setFormData({...formData, isActive: e.target.checked})} />
                    <label className="form-check-label small fw-bold text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>Active Status</label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-light w-100 fw-bold" style={{ borderRadius: '8px' }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary w-100 fw-bold" style={{ backgroundColor: '#0f172a', borderRadius: '8px' }} disabled={status === 'loading'}>
                  {status === 'loading' ? 'Processing...' : 'Save Job Title'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default JobTitle;